import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../../context/UserContext';
import { resolveLorRosterId } from '../../utils/lorIdentity';

const LS_BASE   = 'lor_selected_manager';
const LS_LEGACY = 'lor_selected_roster_id';

// Authenticated users get a user-scoped key so sessions never cross-contaminate.
function scopedKey(userId) {
  return userId ? `${LS_BASE}_${userId}` : null;
}

// Same commissioner check used throughout the codebase.
function isCommissioner(user) {
  if (!user || user.guest) return false;
  return (
    user.display_name === 'PhillyxCommish' ||
    user.username     === 'PhillyxCommish'
  );
}

export default function LORManagerSelector({ api, onSelect, selectedRosterId }) {
  const { currentUser } = useUser();
  const [rosters,    setRosters]    = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [resolved,   setResolved]   = useState(false);
  const prevUserIdRef = useRef(undefined);

  // Reset resolution whenever the logged-in user changes so the incoming user
  // always gets fresh identity resolution — never inherits a prior session.
  useEffect(() => {
    const uid = currentUser?.user_id ?? null;
    if (prevUserIdRef.current !== undefined && prevUserIdRef.current !== uid) {
      setResolved(false);
      setShowPicker(false);
      onSelect(null);
    }
    prevUserIdRef.current = uid;
  }, [currentUser?.user_id, onSelect]);

  useEffect(() => {
    fetch(`${api}/api/lor/rosters`)
      .then(r => r.json())
      .then(d => setRosters(d || []))
      .catch(() => {});
  }, [api]);

  useEffect(() => {
    if (resolved || !rosters.length) return;

    const uid   = currentUser?.user_id ?? null;
    const guest = !currentUser || currentUser.guest;
    const key   = scopedKey(uid);

    // Authenticated users only read from their own scoped key — the global key
    // is never trusted because it may belong to a different user on this device.
    const raw = key
      ? localStorage.getItem(key)
      : guest ? (localStorage.getItem(LS_BASE) || localStorage.getItem(LS_LEGACY)) : null;

    // Session membership is authoritative; legacy users fall back to their
    // Sleeper user_id. A stored selection is only considered after both.
    const ownRosterId = resolveLorRosterId(currentUser, rosters);
    if (ownRosterId !== null) {
      if (key) {
        const storedId = raw ? parseInt(raw, 10) : null;
        if (storedId !== null && storedId !== ownRosterId) localStorage.removeItem(key);
        localStorage.setItem(key, ownRosterId);
      }
      onSelect(ownRosterId);
      setResolved(true);
      return;
    }

    const mayUseManualSelection = guest || isCommissioner(currentUser);
    const storedRosterId = mayUseManualSelection
      ? resolveLorRosterId(currentUser, rosters, raw)
      : null;
    if (storedRosterId !== null) {
      onSelect(storedRosterId);
      setResolved(true);
      return;
    }

    if (raw) {
      if (key) localStorage.removeItem(key);
      if (guest) {
        localStorage.removeItem(LS_BASE);
        localStorage.removeItem(LS_LEGACY);
      }
    }

    // No team found.
    //    Authenticated non-commissioners with no match see an error state —
    //    never another user's data. Commissioners and guests see the full picker.
    setShowPicker(true);
    setResolved(true);
  }, [rosters, currentUser, resolved, onSelect]);

  const handlePick = (id) => {
    const uid = currentUser?.user_id ?? null;
    const key = scopedKey(uid) || LS_BASE;
    localStorage.setItem(key, id);
    onSelect(id);
    setShowPicker(false);
  };

  if (!rosters.length) return null;

  if (showPicker) {
    const guest = !currentUser || currentUser.guest;
    if (!guest && !isCommissioner(currentUser)) {
      // Authenticated non-commissioner with no team in this league.
      return <NoTeamFound />;
    }
    return <IdentityPicker rosters={rosters} onPick={handlePick} />;
  }

  const selected = rosters.find(r => r.roster_id === selectedRosterId);
  if (!selected) return null;

  return (
    <div className="lor-manager-selector">
      <label className="lor-manager-selector-label">Your team:</label>
      <span style={{ color: 'var(--lor-accent)', fontWeight: 600, fontSize: '0.9rem' }}>
        {selected.display_name}
      </span>
    </div>
  );
}

function NoTeamFound() {
  return (
    <div style={{
      padding: '12px 16px', borderRadius: 8,
      background: 'rgba(226,75,74,0.06)', border: '1px solid rgba(226,75,74,0.2)',
      fontSize: '0.85rem', color: '#aaa',
    }}>
      No team found for your account in this league.
      Contact the commissioner if you believe this is an error.
    </div>
  );
}

function IdentityPicker({ rosters, onPick }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0, 0, 0, 0.93)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }}>
      <div style={{
        background: '#12121f',
        border: '1px solid #2a2a4a',
        borderRadius: 16,
        padding: '40px 28px',
        maxWidth: 500,
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 12px 48px rgba(0,0,0,0.7)',
      }}>
        <div style={{ fontSize: 44, marginBottom: 10 }}>🏈</div>
        <h2 style={{
          color: 'var(--lor-gold)', fontSize: '1.4rem', fontWeight: 700,
          marginBottom: 6, letterSpacing: '-0.02em',
        }}>
          Who are you?
        </h2>
        <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: 28 }}>
          Select your team. You'll only need to do this once.
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 10,
        }}>
          {rosters.map(r => (
            <ManagerButton key={r.roster_id} roster={r} onPick={onPick} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ManagerButton({ roster, onPick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={() => onPick(roster.roster_id)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: hover ? '#1a1a3e' : '#0d0d1e',
        border: `1px solid ${hover ? 'var(--lor-gold)' : '#2a2a4a'}`,
        borderRadius: 10,
        padding: '12px 10px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        color: hover ? '#f1f5f9' : '#cbd5e1',
        fontSize: '0.85rem',
        fontWeight: 500,
        textAlign: 'left',
        transition: 'border-color 0.15s, background 0.15s, color 0.15s',
        width: '100%',
      }}
    >
      {roster.avatar ? (
        <img
          src={`https://sleepercdn.com/avatars/thumbs/${roster.avatar}`}
          alt={roster.display_name}
          style={{ width: 30, height: 30, borderRadius: '50%', flexShrink: 0 }}
          onError={e => { e.target.style.display = 'none'; }}
        />
      ) : (
        <div style={{
          width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
          background: '#2a2a4a', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: '0.75rem', color: '#64748b',
        }}>
          {(roster.display_name || '?')[0].toUpperCase()}
        </div>
      )}
      <span style={{ lineHeight: 1.25 }}>{roster.display_name}</span>
    </button>
  );
}
